/**
 * Saved Connections Service
 * Persists previously connected hubs so the connection screen can show them.
 *
 * Storage backends:
 *  - Capacitor: @capacitor/preferences (native key-value store on device)
 *  - Electron: IPC to main-process storage (existing electron.getSavedHubs API)
 *  - Web: localStorage
 */

import { isElectron, isCapacitor } from '../config/runtime';
import { storeSecure, getSecure, deleteSecure } from './secureStorage';
import { PROFILE_DEVICE, activeProfileBucket, activeProfileId, profileScopedKey } from './profileScope';
import { z } from 'zod';

export interface StatusSummary {
  alerts: number;
  warnings: number;
  errors: number;
}

export interface SavedConnection {
  /** Unique id: ip:port for local, hubId for cloud */
  id: string;
  name: string;
  /** Local hub IP address */
  ip?: string;
  /** Local hub port */
  port?: number;
  /**
   * The scheme this address was last reached on (card `t_cf6ea0e5`): `true` =
   * TLS (`https://`), `false` = cleartext, `undefined` = a connection saved
   * before the scheme was carried, or one whose transport nobody measured.
   * Callers must keep the three apart — `undefined` is not a claim of cleartext.
   */
  secure?: boolean;
  /** Cloud hub ID (for WebRTC connections) */
  hubId?: string;
  /** Whether this is a cloud connection */
  isCloud: boolean;
  /** Server image URL or base64 */
  image?: string;
  /** Server icon identifier (from the app icon set) */
  icon?: string;
  /** Last time the user connected */
  lastConnected: string; // ISO date string
  /** Saved username (email) for automatic login */
  username?: string;
  /** Whether a password is stored in secure storage */
  hasPassword?: boolean;
  /** Cached status summary */
  statusSummary?: StatusSummary;
}

const STORAGE_KEY = 'otto_saved_connections';

/**
 * The key **this profile's** saved connections are held under: the bare key for a
 * device with no account, `<key>:<accountId>` for a signed-in one
 * (`services/profileScope.ts`). The bare key is the **device** bucket — every
 * connection saved before this module existed is already there, so a signed-out
 * device paints exactly the list it always painted.
 */
function storageKey(profileId: string | null = activeProfileId()): string {
  return profileScopedKey(STORAGE_KEY, profileId);
}

// ─────────────────────────────────────────────────────────────────────────────
// The main process's payload — parsed, not trusted
// ─────────────────────────────────────────────────────────────────────────────

/**
 * `electron.getSavedHubs()` answers `unknown` on purpose: the bridge hands over
 * unvalidated JSON from another process, and its shape has changed under the
 * renderer (current builds answer `{ hubs, autoConnect, lastConnectedHubId }`,
 * older ones the bare array). This is the boundary this app parses at — zod is
 * already a dependency for exactly this — so both spellings are stated once here
 * and everything below the schema reads a typed row.
 */
const savedHubRecordSchema = z.object({
  id: z.string().optional(),
  name: z.string().optional(),
  ip: z.string().optional(),
  port: z.number().optional(),
  secure: z.boolean().optional(),
  hubId: z.string().optional(),
  isCloud: z.boolean().optional(),
  image: z.string().optional(),
  icon: z.string().optional(),
  lastConnected: z.string().optional(),
  statusSummary: z.object({ alerts: z.number(), warnings: z.number(), errors: z.number() }).optional(),
});

const savedHubsPayloadSchema = z.union([
  z.array(savedHubRecordSchema),
  z.object({ hubs: z.array(savedHubRecordSchema) }),
]);

type SavedHubRecordPayload = z.infer<typeof savedHubRecordSchema>;

/** A main-process hub record, as the renderer's own saved-connection row. */
function toSavedConnection(record: SavedHubRecordPayload): SavedConnection {
  return {
    id: record.id || `${record.ip}:${record.port}`,
    name: record.name ?? '',
    ip: record.ip,
    port: record.port,
    // Three-valued, exactly as the renderer's own records are: a main process
    // that does not carry the scheme must not be read as a claim of cleartext
    // (card `t_cf6ea0e5`).
    secure: record.secure,
    hubId: record.hubId,
    isCloud: record.isCloud ?? false,
    image: record.image,
    icon: record.icon,
    statusSummary: record.statusSummary,
    lastConnected: record.lastConnected || new Date().toISOString(),
  };
}

// ─────────────────────────────────────────────────────────────────────────────
// Storage helpers
// ─────────────────────────────────────────────────────────────────────────────

async function readAll(): Promise<SavedConnection[]> {
  try {
    if (isCapacitor()) {
      const { Preferences } = await import('@capacitor/preferences');
      const { value } = await Preferences.get({ key: storageKey() });
      return value ? JSON.parse(value) : [];
    }

    if (isElectron()) {
      const electron = window.electron;
      if (electron?.getSavedHubs) {
        // The main process keeps one bucket per profile (`electron/main.ts`).
        const payload = savedHubsPayloadSchema.safeParse(await electron.getSavedHubs(activeProfileBucket()));
        if (payload.success) {
          const list = Array.isArray(payload.data) ? payload.data : payload.data.hubs;
          return list.map(toSavedConnection);
        }
      }
    }

    // Web fallback: localStorage
    const raw = localStorage.getItem(storageKey());
    return raw ? JSON.parse(raw) : [];
  } catch {
    return [];
  }
}

async function writeAll(connections: SavedConnection[], profileId: string | null = activeProfileId()): Promise<void> {
  try {
    // The bucket an explicit profile names, or this device's when there is none.
    const bucket = profileId ?? PROFILE_DEVICE;
    if (isCapacitor()) {
      const { Preferences } = await import('@capacitor/preferences');
      await Preferences.set({ key: storageKey(profileId), value: JSON.stringify(connections) });
      return;
    }

    if (isElectron()) {
      const electron = window.electron;
      if (electron?.saveHub) {
        // Electron saves one hub at a time; we only write new/updated ones
        // (full list is managed by Electron side — here we sync the diff)
        await electron.saveAllHubs?.(connections, bucket);
        return;
      }
    }

    localStorage.setItem(storageKey(profileId), JSON.stringify(connections));
  } catch (error) {
    console.warn('[SavedConnections] Failed to persist connections:', error);
  }
}

// ─────────────────────────────────────────────────────────────────────────────
// Public API
// ─────────────────────────────────────────────────────────────────────────────

export async function getSavedConnections(): Promise<SavedConnection[]> {
  const all = await readAll();
  // Sort by most-recently connected
  return all.sort((a, b) => new Date(b.lastConnected).getTime() - new Date(a.lastConnected).getTime());
}

export async function saveConnection(conn: Omit<SavedConnection, 'lastConnected'>): Promise<void> {
  const all = await readAll();
  const idx = all.findIndex((c) => c.id === conn.id);
  const entry: SavedConnection = { ...conn, lastConnected: new Date().toISOString() };
  if (idx >= 0) {
    all[idx] = entry;
  } else {
    all.unshift(entry);
  }
  // A local hub reached via a different IP (e.g. localhost vs LAN IP) is the
  // same server — replace stale duplicates that share the same hubId so the
  // connection screen shows one card per hub.
  if (conn.hubId && !conn.isCloud) {
    const deduped = all.filter((c) => !(!c.isCloud && c.hubId === conn.hubId) || c.id === conn.id);
    await writeAll(deduped.slice(0, 20));
    return;
  }
  // Keep only last 20
  await writeAll(all.slice(0, 20));
}

export async function deleteConnection(id: string): Promise<void> {
  const all = await readAll();
  await writeAll(all.filter((c) => c.id !== id));
  // Clean up stored credentials
  await deleteConnectionCredentials(id);
}

/**
 * Replace the saved connections list wholesale (used to backfill hub IDs
 * learned from discovery so saved↔cloud↔discovered cards merge).
 */
export async function updateSavedConnections(connections: SavedConnection[]): Promise<void> {
  await writeAll(connections.slice(0, 20));
}

// ─────────────────────────────────────────────────────────────────────────────
// Credential storage (secure, platform-specific)
// ─────────────────────────────────────────────────────────────────────────────

const CRED_USERNAME_SUFFIX = '_username';
const CRED_PASSWORD_SUFFIX = '_password';

/**
 * The credential key for one connection — held **per profile**, like the lists:
 * one account's stored hub password must not sign another account in. The bare
 * form is the device bucket, where credentials stored before this module existed
 * already are.
 *
 * The profile is a **prefix** here rather than the suffix the list keys use: the
 * base is already composite (`<connectionId><_field>`), so putting the profile in
 * front keeps the connection id and its field adjacent — `dev-hub:80_username`
 * stays readable — and groups one account's credentials together in devtools.
 */
function credentialKey(connectionId: string, suffix: string): string {
  const profileId = activeProfileId();
  return profileId ? `${profileId}:${connectionId}${suffix}` : `${connectionId}${suffix}`;
}

/**
 * Store username and password for a saved connection.
 * Username is stored in the connection record itself; password in secure storage.
 */
export async function saveConnectionCredentials(
  connectionId: string,
  username: string,
  password: string,
): Promise<void> {
  await storeSecure(credentialKey(connectionId, CRED_USERNAME_SUFFIX), username);
  await storeSecure(credentialKey(connectionId, CRED_PASSWORD_SUFFIX), password);

  // Update the connection record to reflect stored credentials
  const all = await readAll();
  const idx = all.findIndex((c) => c.id === connectionId);
  if (idx >= 0) {
    all[idx] = {
      ...all[idx],
      username,
      hasPassword: !!password,
    };
    await writeAll(all);
  }
}

/**
 * Retrieve stored credentials for a connection.
 * Returns null for both fields if nothing is stored.
 */
export async function getConnectionCredentials(
  connectionId: string,
): Promise<{ username: string | null; password: string | null }> {
  const [username, password] = await Promise.all([
    getSecure(credentialKey(connectionId, CRED_USERNAME_SUFFIX)),
    getSecure(credentialKey(connectionId, CRED_PASSWORD_SUFFIX)),
  ]);
  return { username, password };
}

/**
 * Delete stored credentials for a connection.
 *
 * Sequential, not `Promise.all`: on Electron each delete is a whole-file
 * read-modify-write in the main process, and two interleaved ones lose one of the
 * two removals (measured live 2026-10-04 — the username survived the password's
 * delete). The main process serializes its handlers too; this keeps the two calls
 * from being issued at the same instant in the first place.
 */
export async function deleteConnectionCredentials(connectionId: string): Promise<void> {
  await deleteSecure(credentialKey(connectionId, CRED_USERNAME_SUFFIX));
  await deleteSecure(credentialKey(connectionId, CRED_PASSWORD_SUFFIX));

  // Update the connection record to remove credential flags
  const all = await readAll();
  const idx = all.findIndex((c) => c.id === connectionId);
  if (idx >= 0) {
    all[idx] = {
      ...all[idx],
      username: undefined,
      hasPassword: false,
    };
    await writeAll(all);
  }
}

export async function getAutoConnectHub(): Promise<SavedConnection | null> {  try {
    if (isElectron()) {
      const electron = window.electron;
      // The main process answers one hub record (or null) — same schema, same
      // reader as the saved-hubs list above.
      const autoHub = savedHubRecordSchema.safeParse(await electron?.getAutoConnectHub?.(activeProfileBucket()));
      if (autoHub.success) return toSavedConnection(autoHub.data);
    }
    // Always return the most-recently connected hub for now
    const all = await getSavedConnections();
    return all[0] ?? null;
  } catch {
    return null;
  }
}

/**
 * Take away the **device bucket's** saved connections — the list a device with no
 * account keeps. Called when the cloud account goes away
 * (`services/signedOutLists.ts`): the owner's decision is that a signed-out device
 * shows **no** servers, so the rows a previous user left behind cannot be painted
 * for the next person to look at the screen.
 *
 * An account's bucket is never touched — signing out is not a delete, and the
 * account's own list must be exactly as it was left when it signs back in.
 *
 * The credentials stored for those connections (`otto_creds_…`) are keyed by
 * connection id + profile, so they become unreachable with their connection and
 * are left alone rather than enumerated (the main process has no listing API for
 * its encrypted store).
 */
export async function clearDeviceSavedConnections(): Promise<void> {
  await writeAll([], null);
}
